import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsalAuthentication,
} from "@azure/msal-react";
import "App.css";
import React, { lazy, Suspense } from "react";
import { Login } from "Pages/Login";
// import { AdvancedTheme } from "@nokia-csf-uxr/ccfk";

import AdvancedTheme from '@nokia-csf-uxr/ccfk/AdvancedTheme';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
// import { BrowserRouter, Route, Routes, useNavigate } from "react-router-dom";
import { BrowserRouter } from "react-router-dom";
import "@nokia-csf-uxr/nokia-design-system-tokens/css/_variables.css";
import "@nokia-csf-uxr/nokia-design-system-tokens/global-style.css";
import { InteractionType } from "@azure/msal-browser";
import { Landing } from "Pages/Landing";
import Loader from "Components/Elements/Loader";
import { useMaintenanceCheck } from "hooks/useMaintenanceCheck";
import MaintenancePage from "Pages/Maintenance/MaintenancePage";
import ChartJsBarDemo from "Components/Features/ChartJsBarDemo";

const LandingPageWithHeader = lazy(
  () => import("Pages/Landing/LandingPageWithHeader"),
);

const ReactQueryDevtools =
  process.env.NODE_ENV === 'development'
    ? lazy(() =>
        import('@tanstack/react-query-devtools').then((m) => ({
          default: m.ReactQueryDevtools,
        })),
      )
    : () => null;

/**
 * AuthApp — contains all MSAL-dependent logic.
 *
 * Kept as a separate component so that useMsalAuthentication (and the Azure
 * Entra redirect flow) is NEVER called while the app is in maintenance mode.
 * React's Rules of Hooks forbid conditional hook calls, so the guard is
 * achieved by mounting this component only when maintenance mode is false.
 */
const AuthApp: React.FC = () => {
  const accessToken =  new URLSearchParams(window.location.search).get("token");
  useMsalAuthentication(accessToken === null ? InteractionType.Redirect : InteractionType.None); //to auto redired login
  
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: { refetchOnWindowFocus: false },
    },
  });
  return (
    <>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
        <Suspense fallback={null}>
            <ReactQueryDevtools initialIsOpen={false} />
        </Suspense>
          
          <AdvancedTheme
            advancedTheme="CCFK FreeForm - Light"
            addGlobalStyle={false}
            addGlobalVariables={true}
          >
            <AuthenticatedTemplate>
              <Landing />
            </AuthenticatedTemplate>
            <UnauthenticatedTemplate>
              {accessToken != "" ? (
                <Suspense
                  fallback={
                    <div className="flex-center-screen">
                      <Loader />
                    </div>
                  }
                >
                  <LandingPageWithHeader accessToken={accessToken!} />
                </Suspense>
              ) : (
                <Login />
              )}
            </UnauthenticatedTemplate>
          </AdvancedTheme>
        </QueryClientProvider>
      </BrowserRouter>
    </>
  );
};

/**
 * EnsoBp — application root.
 *
 * Performs a maintenance check BEFORE mounting any auth-related logic.
 * Flow:
 *   1. On mount: fetch system-status.json (5 s timeout, fail-open)
 *   2. isLoading  → render nothing (avoids flash of either page)
 *   3. isMaintenance === true  → render <MaintenancePage>, skip auth entirely
 *   4. isMaintenance === false → render <AuthApp>, existing flow unchanged
 */
const EnsoBp: React.FC = () => {
  const { isLoading, isMaintenance } = useMaintenanceCheck();

  if (isLoading) {
    // Blank while the status check resolves — typically < 200 ms on a healthy network.
    // Avoids flashing the login/redirect flow before we know the maintenance state.
    return null;
  }

  if (isMaintenance) {
    return <MaintenancePage />;
  }

  return <AuthApp />;
};

export default EnsoBp;
